import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft,
  faCheck,
  faCircleCheck,
  faEnvelope,
  faGift,
  faPlus,
  faRotate,
  faStop,
  faTrashCan,
  faTriangleExclamation,
  faUserCheck,
  faHourglassHalf,
  faPen,
  faXmark,
} from '@fortawesome/free-solid-svg-icons';
import { faWhatsapp } from '@fortawesome/free-brands-svg-icons';
import './WinBack.css';

const API = `${process.env.REACT_APP_API_BASE_URL}/api/admin/winback`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('userData')}` } });

/* ---------- dates (Beirut days, YYYY-MM-DD) ---------- */
const today = () => new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Beirut' });
const addDays = (day, n) => {
  const d = new Date(`${day}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
};
const addMonths = (day, n) => {
  const d = new Date(`${day}T00:00:00Z`);
  d.setUTCMonth(d.getUTCMonth() + n);
  return d.toISOString().slice(0, 10);
};
const dayLabel = (day) =>
  day ? new Date(`${day}T12:00:00Z`).toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric', timeZone: 'UTC' }) : '';
const shortDate = (v) => {
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

/* ---------- messages ---------- */
const STEPS = [
  { n: 1, label: 'The offer', when: 'Send now' },
  { n: 2, label: 'Reminder', when: 'On the reminder day you pick' },
  { n: 3, label: 'Last reminder', when: 'Usually the day before the end' },
];

const DEFAULTS = [
  {
    subject: '🎁 A gift for {child}: {offer} off at RoboMind',
    text:
      'Hi! 👋 It’s RoboMind Academy.\n\n' +
      '{child} did a free coding class with us, and we’d love to have them back! 🚀\n\n' +
      'For a few days only, {child} gets *{offer} off* our coding packages 🎁\n\n' +
      'Choose the package and tell us the days and times that suit you:\n{link}\n\n' +
      '_The offer ends on {deadline}._',
  },
  {
    subject: '⏳ A few days left: {offer} off for {child}',
    text:
      'Hi! Just a reminder from RoboMind 😊\n\n' +
      'The *{offer} off* for {child} ends on {deadline}.\n\n' +
      'You can enroll in 2 minutes here:\n{link}',
  },
  {
    subject: '⏰ Last day tomorrow for {child}’s offer',
    text:
      'Hi! The *{offer} off* for {child} ends tomorrow ({deadline}) ⏰\n\n' +
      'If you’d like {child} to start coding with us, here’s the link:\n{link}\n\n' +
      '_Any question? Just reply here_ 🙏',
  },
];

const fill = (text, v) =>
  String(text || '')
    .replace(/\{child\}/g, v.child || '')
    .replace(/\{teacher\}/g, v.teacher || '')
    .replace(/\{link\}/g, v.link || '')
    .replace(/\{deadline\}/g, v.deadline || '')
    .replace(/\{offer\}/g, v.offer || '');

const varsFor = (campaign, f) => ({
  child: f.childFirst || String(f.child || '').split(' ')[0] || 'your child',
  teacher: f.teacherFirst || String(f.teacher || '').split(' ')[0] || '',
  link: f.link || '',
  deadline: dayLabel(campaign.endsOn),
  offer: campaign.offerPercent ? `${campaign.offerPercent}%` : '',
});

/* WhatsApp number: digits with the country code (Lebanese numbers without it get 961) */
const waNumber = (raw) => {
  let d = String(raw || '').replace(/[^\d+]/g, '');
  if (!d) return null;
  if (d.startsWith('+')) d = d.slice(1);
  else if (d.startsWith('00')) d = d.slice(2);
  else if (d.startsWith('961')) {
    /* already has it */
  } else if (d.startsWith('0') && d.length <= 9) d = `961${d.slice(1)}`;
  else if (d.length <= 8) d = `961${d}`;
  return d.length >= 8 ? d : null;
};
// on a computer: WhatsApp Web (it keeps the line breaks of the message); on a phone: the WhatsApp app
const onPhone = () => typeof navigator !== 'undefined' && /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent || '');
const waLink = (phone, text) => {
  const n = waNumber(phone);
  if (!n) return null;
  const msg = encodeURIComponent(String(text).replace(/\r\n?/g, '\n'));
  return onPhone() ? `https://api.whatsapp.com/send?phone=${n}&text=${msg}` : `https://web.whatsapp.com/send?phone=${n}&text=${msg}`;
};

// offer length -> last day (day 1 = today) and the default reminder days (halfway, the day before the end)
const endFor = (days) => addDays(today(), days - 1);
const remindersFor = (end) => {
  const start = today();
  const days = Math.round((new Date(`${end}T00:00:00Z`) - new Date(`${start}T00:00:00Z`)) / 86400000) + 1;
  const r3 = addDays(end, -1);
  const r2 = addDays(end, -Math.floor(days / 2));
  return { r2: r2 > start && r2 < r3 ? r2 : '', r3: r3 > start ? r3 : '' };
};

const errText = (err, fallback) => err?.response?.data?.error || fallback;

/* ---------- the 3 messages editor (used by "new offer" and "edit messages") ---------- */
const MessagesEditor = ({ messages, onChange, sample }) => (
  <div className="rmwb-msgs">
    <p className="rmwb-hint">
      Use <code>{'{child}'}</code> <code>{'{link}'}</code> <code>{'{deadline}'}</code> <code>{'{offer}'}</code>: each family gets its own. <code>*bold*</code> and <code>_italic_</code> work in WhatsApp and in the email.
      Better not to name the demo teacher: they may not be free any more, and you choose the teacher when the family enrolls. WhatsApp sends the text; the email adds the subject and an “See the offer” button.
    </p>
    {STEPS.map((s, i) => (
      <div key={s.n} className="rmwb-msg">
        <div className="rmwb-msg-head">
          <b>
            {s.n}. {s.label}
          </b>
          <span>{s.when}</span>
        </div>
        <input
          type="text"
          className="rmwb-input"
          placeholder="Email subject"
          value={messages[i].subject}
          onChange={(e) => onChange(messages.map((m, j) => (j === i ? { ...m, subject: e.target.value } : m)))}
        />
        <textarea
          className="rmwb-input"
          rows={6}
          dir="auto"
          value={messages[i].text}
          onChange={(e) => onChange(messages.map((m, j) => (j === i ? { ...m, text: e.target.value } : m)))}
        />
        {sample && messages[i].text && (
          <details className="rmwb-preview">
            <summary>Preview for {sample.child}</summary>
            <p dir="auto">{fill(messages[i].text, sample)}</p>
          </details>
        )}
      </div>
    ))}
  </div>
);

/* ---------- new offer ---------- */
const NewOffer = ({ onCreated, onCancel }) => {
  const [from, setFrom] = useState(addMonths(today(), -6));
  const [to, setTo] = useState(addDays(today(), -30));
  const [families, setFamilies] = useState(null);
  const [picked, setPicked] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [name, setName] = useState('');
  const [percent, setPercent] = useState(20);
  const [endsOn, setEndsOnRaw] = useState(endFor(5));
  const [remind, setRemind] = useState(() => remindersFor(endFor(5))); // { r2, r3 } ('' = no reminder)
  const setEndsOn = (day) => {
    setEndsOnRaw(day);
    setRemind(remindersFor(day));
  };
  const [messages, setMessages] = useState(DEFAULTS);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.get(`${API}/candidates`, { ...auth(), params: { from, to } });
      const list = res.data?.families || [];
      setFamilies(list);
      setPicked(new Set(list.filter((f) => !f.paidFamily && !f.inOffer).map((f) => f.id)));
    } catch (err) {
      setError(errText(err, 'Couldn’t load the families.'));
    } finally {
      setLoading(false);
    }
  };

  const toggle = (id) =>
    setPicked((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });

  const sample = useMemo(() => {
    const f = (families || []).find((x) => picked.has(x.id));
    if (!f) return null;
    return varsFor({ endsOn, offerPercent: Number(percent) || 0 }, { child: f.child, teacher: f.teacher, link: 'https://robomind-edu.com/feedback/…' });
  }, [families, picked, endsOn, percent]);

  const create = async () => {
    setSaving(true);
    setError('');
    try {
      const res = await axios.post(
        `${API}/campaigns`,
        {
          name,
          demoFrom: from,
          demoTo: to,
          offerPercent: Number(percent) || 0,
          endsOn,
          remind2On: remind.r2 || null,
          remind3On: remind.r3 || null,
          messages,
          studentIds: [...picked],
        },
        auth()
      );
      onCreated(res.data.id);
    } catch (err) {
      setError(errText(err, 'Couldn’t create the offer.'));
      setSaving(false);
    }
  };

  const n = picked.size;
  return (
    <div className="rmwb-new">
      <button type="button" className="rmwb-back" onClick={onCancel}>
        <FontAwesomeIcon icon={faArrowLeft} /> All offers
      </button>
      <h2>New win-back offer</h2>

      <section className="rmwb-card">
        <h3>1. Families: free class done, never enrolled</h3>
        <div className="rmwb-row">
          <label>
            <span>Demos from</span>
            <input type="date" className="rmwb-input" value={from} max={to} onChange={(e) => setFrom(e.target.value)} />
          </label>
          <label>
            <span>to</span>
            <input type="date" className="rmwb-input" value={to} min={from} onChange={(e) => setTo(e.target.value)} />
          </label>
          <button type="button" className="rmwb-btn" onClick={load} disabled={loading || !from || !to}>
            <FontAwesomeIcon icon={faRotate} spin={loading} /> {families ? 'Load again' : 'Show families'}
          </button>
        </div>
        {families && (
          <>
            <p className="rmwb-count">
              <b>{families.length}</b> {families.length === 1 ? 'family' : 'families'} · <b>{n}</b> chosen
              {families.length > 0 && (
                <>
                  {' · '}
                  <button type="button" className="rmwb-link" onClick={() => setPicked(new Set(families.map((f) => f.id)))}>
                    all
                  </button>
                  {' · '}
                  <button type="button" className="rmwb-link" onClick={() => setPicked(new Set())}>
                    none
                  </button>
                </>
              )}
            </p>
            {families.length > 0 && (
              <div className="rmwb-table-wrap">
                <table className="rmwb-table">
                  <thead>
                    <tr>
                      <th aria-label="Chosen" />
                      <th>Child</th>
                      <th>Demo</th>
                      <th>Teacher</th>
                      <th>Result</th>
                      <th>Contact</th>
                    </tr>
                  </thead>
                  <tbody>
                    {families.map((f) => (
                      <tr key={f.id} className={picked.has(f.id) ? '' : 'is-off'}>
                        <td>
                          <input type="checkbox" checked={picked.has(f.id)} onChange={() => toggle(f.id)} aria-label={`Choose ${f.child}`} />
                        </td>
                        <td>
                          <b>{f.child}</b>
                          {f.paidFamily && <span className="rmwb-tag is-warn">Has a paid sibling</span>}
                          {f.inOffer && <span className="rmwb-tag is-warn">In “{f.inOffer}”</span>}
                        </td>
                        <td>{shortDate(f.demoAt)}</td>
                        <td>{f.teacher || '—'}</td>
                        <td>
                          <span className="rmwb-tag">{f.result}</span>
                        </td>
                        <td className="rmwb-contact">
                          {f.phone ? <FontAwesomeIcon icon={faWhatsapp} title={f.phone} /> : <FontAwesomeIcon icon={faWhatsapp} className="is-missing" title="No phone" />}
                          {f.email ? <FontAwesomeIcon icon={faEnvelope} title={f.email} /> : <FontAwesomeIcon icon={faEnvelope} className="is-missing" title="No email" />}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </section>

      <section className="rmwb-card">
        <h3>2. The offer</h3>
        <div className="rmwb-row">
          <label>
            <span>Discount</span>
            <span className="rmwb-suffix">
              <input type="number" className="rmwb-input" min="0" max="90" value={percent} onChange={(e) => setPercent(e.target.value)} />%
            </span>
          </label>
          <div className="rmwb-length">
            <span>How long</span>
            <div className="rmwb-filters">
              {[3, 5, 7].map((n) => (
                <button key={n} type="button" className={endsOn === endFor(n) ? 'is-on' : ''} onClick={() => setEndsOn(endFor(n))}>
                  {n} days
                </button>
              ))}
            </div>
          </div>
          <label>
            <span>Last day of the offer</span>
            <input type="date" className="rmwb-input" min={today()} value={endsOn} onChange={(e) => e.target.value && setEndsOn(e.target.value)} />
          </label>
          <label className="rmwb-grow">
            <span>Name (only for you)</span>
            <input type="text" className="rmwb-input" placeholder={`Offer until ${endsOn}`} value={name} onChange={(e) => setName(e.target.value)} />
          </label>
        </div>
        <p className="rmwb-hint">
          {Number(percent) > 0
            ? `Their enroll link shows the prices with ${percent}% off until ${dayLabel(endsOn)}, then goes back to normal. Families who enroll before keep the price.`
            : 'No discount: the link shows the normal prices.'}{' '}
          On the link the family picks a package and tells you the days, time of day and classes per week that suit them (Monday to
          Sunday); you then choose the teacher and confirm the times. The old demo teacher gets no package bonus unless you tick it when they pay.
          The offer ends on <b>{dayLabel(endsOn)}</b>
          {new Date(`${endsOn}T12:00:00Z`).getUTCDay() === 0 ? ' (a Sunday: most families don’t pay on Sundays, maybe end it on Friday or Saturday)' : ''}.
        </p>
        <div className="rmwb-row">
          <label>
            <span>Reminder</span>
            <input type="date" className="rmwb-input" min={addDays(today(), 1)} max={endsOn} value={remind.r2} onChange={(e) => setRemind((r) => ({ ...r, r2: e.target.value }))} />
          </label>
          <label>
            <span>Last reminder</span>
            <input type="date" className="rmwb-input" min={addDays(today(), 1)} max={endsOn} value={remind.r3} onChange={(e) => setRemind((r) => ({ ...r, r3: e.target.value }))} />
          </label>
        </div>
        <p className="rmwb-hint">Clear a date if you don’t want that reminder.</p>
      </section>

      <section className="rmwb-card">
        <h3>3. The messages</h3>
        <MessagesEditor messages={messages} onChange={setMessages} sample={sample} />
      </section>

      {error && (
        <p className="rmwb-error">
          <FontAwesomeIcon icon={faTriangleExclamation} /> {error}
        </p>
      )}
      <div className="rmwb-actions">
        <button type="button" className="rmwb-btn rmwb-btn--ghost" onClick={onCancel}>
          Cancel
        </button>
        <button type="button" className="rmwb-btn rmwb-btn--yellow" onClick={create} disabled={saving || !n || !messages[0].text.trim()}>
          <FontAwesomeIcon icon={faGift} /> {saving ? 'Creating…' : `Create the offer for ${n} ${n === 1 ? 'family' : 'families'}`}
        </button>
      </div>
    </div>
  );
};

/* ---------- one offer: send, follow up ---------- */
const FILTERS = [
  ['todo', 'To do today'],
  ['all', 'All'],
  ['enrolled', 'Enrolled'],
];

const OfferView = ({ id, onBack }) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('todo');
  const [busy, setBusy] = useState('');
  const [toast, setToast] = useState(null);
  const [editing, setEditing] = useState(null); // messages being edited
  const [dates, setDates] = useState(null); // { endsOn, r2, r3 } being edited
  const [mailing, setMailing] = useState(null); // { step, done, total }

  const load = useCallback(async () => {
    setError('');
    try {
      const res = await axios.get(`${API}/campaigns/${id}`, auth());
      setData(res.data);
    } catch (err) {
      setError(errText(err, 'Couldn’t load this offer.'));
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!toast) return undefined;
    const t = setTimeout(() => setToast(null), 3500);
    return () => clearTimeout(t);
  }, [toast]);

  if (error && !data) {
    return (
      <div className="rmwb-empty">
        <FontAwesomeIcon icon={faTriangleExclamation} />
        <p>{error}</p>
        <button type="button" className="rmwb-btn" onClick={load}>
          <FontAwesomeIcon icon={faRotate} /> Try again
        </button>
      </div>
    );
  }
  if (!data) return <div className="rmwb-skel" aria-busy="true" />;

  const c = data.campaign;
  const now = today();
  const running = c.status === 'active';
  const isDue = (n) => n === 1 || (c.due[n] && now >= c.due[n]);
  // the next WhatsApp this family needs (null = nothing to do)
  const nextStep = (f) => {
    if (f.converted || !running) return null;
    if (!f.wa[0]) return 1;
    for (const n of [2, 3]) {
      if (!f.wa[n - 1] && isDue(n) && c.messages[n - 1].text.trim()) return n;
    }
    return null;
  };
  const families = data.families;
  const shown = families.filter((f) =>
    filter === 'all' ? true : filter === 'enrolled' ? !!f.converted : nextStep(f) != null
  );
  const counts = {
    todo: families.filter((f) => nextStep(f) != null).length,
    all: families.length,
    enrolled: families.filter((f) => f.converted).length,
  };
  const contacted = families.filter((f) => f.wa[0] || f.mail[0]).length;

  const sendWa = async (f, n) => {
    const text = fill(c.messages[n - 1].text, varsFor(c, f));
    const url = waLink(f.phone, text);
    if (!url) {
      setToast({ msg: `${f.child}: no WhatsApp number.`, bad: true });
      return;
    }
    window.open(url, '_blank', 'noopener'); // first, while the click still counts (pop-up blockers)
    setBusy(`${f.recipientId}-${n}`);
    try {
      const res = await axios.post(`${API}/campaigns/${id}/whatsapp`, { recipientId: f.recipientId, step: n }, auth());
      const mailNote = res.data?.mailed ? ' + email sent' : res.data?.mailError === 'No email' ? ' (no email)' : res.data?.mailError ? ' (email failed)' : '';
      setToast({ msg: `${f.child}: WhatsApp ${n} marked as sent${mailNote}.`, bad: !!res.data?.mailError && res.data.mailError !== 'No email' });
      await load();
    } catch (err) {
      setToast({ msg: errText(err, 'Couldn’t save it.'), bad: true });
    } finally {
      setBusy('');
    }
  };

  const unmark = async (f, n) => {
    setBusy(`${f.recipientId}-${n}`);
    try {
      await axios.post(`${API}/campaigns/${id}/unmark`, { recipientId: f.recipientId, step: n }, auth());
      await load();
    } catch (err) {
      setToast({ msg: errText(err, 'Couldn’t save it.'), bad: true });
    } finally {
      setBusy('');
    }
  };

  const remove = async (f) => {
    if (!window.confirm(`Take ${f.child} out of this offer?`)) return;
    try {
      await axios.delete(`${API}/campaigns/${id}/recipients/${f.recipientId}`, auth());
      await load();
    } catch (err) {
      setToast({ msg: errText(err, 'Couldn’t remove it.'), bad: true });
    }
  };

  // email step n to everyone who hasn't had it (10 at a time)
  const emailAll = async (n) => {
    const list = families.filter((f) => !f.converted && f.email && !f.mail[n - 1] && (n === 1 || f.wa[0] || f.mail[0]));
    if (!list.length) {
      setToast({ msg: 'Everyone with an email already got it.' });
      return;
    }
    if (!window.confirm(`Send email ${n} (“${STEPS[n - 1].label}”) to ${list.length} ${list.length === 1 ? 'family' : 'families'}?`)) return;
    let sent = 0;
    let failed = 0;
    setMailing({ step: n, done: 0, total: list.length });
    for (let i = 0; i < list.length; i += 10) {
      const chunk = list.slice(i, i + 10);
      try {
        const res = await axios.post(`${API}/campaigns/${id}/email`, { step: n, recipientIds: chunk.map((f) => f.recipientId) }, { ...auth(), timeout: 120000 });
        sent += res.data?.sent || 0;
        failed += res.data?.failed || 0;
      } catch {
        failed += chunk.length;
      }
      setMailing({ step: n, done: Math.min(i + 10, list.length), total: list.length });
    }
    setMailing(null);
    setToast({ msg: `${sent} emails sent${failed ? `, ${failed} failed` : ''}.`, bad: failed > 0 });
    load();
  };

  const saveMessages = async () => {
    try {
      await axios.put(
        `${API}/campaigns/${id}`,
        { messages: editing, ...(dates ? { endsOn: dates.endsOn, remind2On: dates.r2 || null, remind3On: dates.r3 || null } : {}) },
        auth()
      );
      setEditing(null);
      setDates(null);
      setToast({ msg: 'Saved.' });
      load();
    } catch (err) {
      setToast({ msg: errText(err, 'Couldn’t save the messages.'), bad: true });
    }
  };

  const stop = async () => {
    if (!window.confirm('Stop this offer? No more reminders, and the links go back to the normal prices.')) return;
    try {
      await axios.post(`${API}/campaigns/${id}/stop`, {}, auth());
      setToast({ msg: 'Offer stopped. You can delete it now if you want.' });
      load();
    } catch (err) {
      setToast({ msg: errText(err, 'Couldn’t stop it.'), bad: true });
    }
  };

  // stopped or ended: delete it (with its list of families)
  const removeOffer = async () => {
    if (
      !window.confirm(
        `Delete “${c.name}”? Its list of families and what was sent are deleted. Families who already enrolled stay enrolled (the payment amount you recorded doesn’t change).`
      )
    )
      return;
    try {
      await axios.delete(`${API}/campaigns/${id}`, auth());
      onBack();
    } catch (err) {
      setToast({ msg: errText(err, 'Couldn’t delete it.'), bad: true });
    }
  };

  const sample = families[0] ? varsFor(c, families[0]) : null;

  return (
    <div className="rmwb-view">
      <button type="button" className="rmwb-back" onClick={onBack}>
        <FontAwesomeIcon icon={faArrowLeft} /> All offers
      </button>
      <header className="rmwb-view-head">
        <div>
          <h2>{c.name}</h2>
          <p>
            {c.offerPercent ? <b>{c.offerPercent}% off</b> : <b>No discount</b>} · until <b>{dayLabel(c.endsOn)}</b>
            {c.demoFrom && c.demoTo ? ` · demos ${shortDate(c.demoFrom)} → ${shortDate(c.demoTo)}` : ''}
          </p>
        </div>
        <span className={`rmwb-status is-${c.status}`}>{c.status === 'active' ? 'Running' : c.status === 'ended' ? 'Ended' : 'Stopped'}</span>
      </header>

      <div className="rmwb-stats">
        <div>
          <strong>{families.length}</strong>
          <span>families</span>
        </div>
        <div>
          <strong>{contacted}</strong>
          <span>contacted</span>
        </div>
        <div className="is-good">
          <strong>{counts.enrolled}</strong>
          <span>enrolled</span>
        </div>
        <div>
          <strong>{running ? counts.todo : '—'}</strong>
          <span>to do today</span>
        </div>
      </div>

      <div className="rmwb-steps-bar">
        {STEPS.map((s) => (
          <div key={s.n} className={`rmwb-step ${isDue(s.n) && running ? 'is-due' : ''}`}>
            <b>
              {s.n}. {s.label}
            </b>
            <span>{s.n === 1 ? 'Now' : dayLabel(c.due[s.n])}</span>
            {running && isDue(s.n) && c.messages[s.n - 1].text.trim() && (
              <button type="button" className="rmwb-link" onClick={() => emailAll(s.n)} disabled={!!mailing}>
                <FontAwesomeIcon icon={faEnvelope} /> Email everyone
              </button>
            )}
          </div>
        ))}
      </div>
      {mailing && (
        <p className="rmwb-hint">
          Sending email {mailing.step}… {mailing.done} / {mailing.total}
        </p>
      )}
      <p className="rmwb-hint">
        Reminder emails go out by themselves at 10:15 (Beirut) on their day, to families you already contacted. The WhatsApp reminders show up in
        “To do today”.
      </p>

      <div className="rmwb-toolbar">
        <div className="rmwb-filters" role="tablist">
          {FILTERS.map(([k, label]) => (
            <button key={k} type="button" role="tab" aria-selected={filter === k} className={filter === k ? 'is-on' : ''} onClick={() => setFilter(k)}>
              {label} <span>{counts[k]}</span>
            </button>
          ))}
        </div>
        <div className="rmwb-toolbar-right">
          <button type="button" className="rmwb-btn rmwb-btn--ghost" onClick={() => {
              setEditing(editing ? null : c.messages);
              setDates(editing ? null : { endsOn: c.endsOn, r2: c.due[2] || '', r3: c.due[3] || '' });
            }}>
            <FontAwesomeIcon icon={faPen} /> {editing ? 'Close' : 'Edit dates & messages'}
          </button>
          {running ? (
            <button type="button" className="rmwb-btn rmwb-btn--ghost" onClick={stop}>
              <FontAwesomeIcon icon={faStop} /> Stop
            </button>
          ) : (
            <button type="button" className="rmwb-btn rmwb-btn--ghost rmwb-btn--danger" onClick={removeOffer}>
              <FontAwesomeIcon icon={faTrashCan} /> Delete offer
            </button>
          )}
        </div>
      </div>

      {editing && (
        <section className="rmwb-card">
          {dates && (
            <div className="rmwb-row rmwb-dates">
              <label>
                <span>Last day of the offer</span>
                <input type="date" className="rmwb-input" value={dates.endsOn} onChange={(e) => e.target.value && setDates((x) => ({ ...x, endsOn: e.target.value }))} />
              </label>
              <label>
                <span>Reminder</span>
                <input type="date" className="rmwb-input" max={dates.endsOn} value={dates.r2} onChange={(e) => setDates((x) => ({ ...x, r2: e.target.value }))} />
              </label>
              <label>
                <span>Last reminder</span>
                <input type="date" className="rmwb-input" max={dates.endsOn} value={dates.r3} onChange={(e) => setDates((x) => ({ ...x, r3: e.target.value }))} />
              </label>
            </div>
          )}
          <MessagesEditor messages={editing} onChange={setEditing} sample={sample} />
          <div className="rmwb-actions">
            <button type="button" className="rmwb-btn rmwb-btn--ghost" onClick={() => { setEditing(null); setDates(null); }}>
              Cancel
            </button>
            <button type="button" className="rmwb-btn" onClick={saveMessages}>
              <FontAwesomeIcon icon={faCheck} /> Save
            </button>
          </div>
        </section>
      )}

      {shown.length === 0 ? (
        <div className="rmwb-empty">
          <FontAwesomeIcon icon={faCircleCheck} />
          <p>{filter === 'todo' ? (running ? 'Nothing to send today. 🎉' : 'This offer is over.') : 'No families here.'}</p>
        </div>
      ) : (
        <ul className="rmwb-families">
          {shown.map((f) => {
            const next = nextStep(f);
            return (
              <li key={f.recipientId} className={`rmwb-family ${f.converted ? 'is-converted' : ''}`}>
                <div className="rmwb-family-who">
                  <Link to={`/Admin/studentprofile/${f.id}`} target="_blank" rel="noopener noreferrer">
                    {f.child}
                  </Link>
                  <small>
                    Demo {shortDate(f.demoAt)}
                    {f.teacher ? ` · ${f.teacher}` : ''}
                  </small>
                  {f.converted === 'enrolled' && (
                    <span className="rmwb-tag is-good">
                      <FontAwesomeIcon icon={faUserCheck} /> Enrolled
                    </span>
                  )}
                  {f.converted === 'pending' && (
                    <span className="rmwb-tag is-blue">
                      <FontAwesomeIcon icon={faHourglassHalf} /> Enrolled from the link · not paid yet
                    </span>
                  )}
                  {!f.phone && <span className="rmwb-tag is-warn">No phone</span>}
                  {f.mailError && f.mailError !== 'No email' && <span className="rmwb-tag is-warn">Email failed</span>}
                </div>
                <div className="rmwb-family-steps">
                  {STEPS.map((s) => {
                    const sentAt = f.wa[s.n - 1];
                    const mailed = f.mail[s.n - 1];
                    const key = `${f.recipientId}-${s.n}`;
                    if (sentAt) {
                      return (
                        <span key={s.n} className="rmwb-sent" title={`WhatsApp ${shortDate(sentAt)}${mailed ? ' · email sent' : ''}`}>
                          <FontAwesomeIcon icon={faCheck} /> {s.n}
                          {mailed && <FontAwesomeIcon icon={faEnvelope} className="rmwb-sent-mail" />}
                          <button type="button" onClick={() => unmark(f, s.n)} disabled={busy === key} aria-label={`Undo WhatsApp ${s.n}`}>
                            <FontAwesomeIcon icon={faXmark} />
                          </button>
                        </span>
                      );
                    }
                    const can = running && !f.converted && f.phone && c.messages[s.n - 1].text.trim() && (s.n === 1 || f.wa[0] || f.mail[0]);
                    return (
                      <button
                        key={s.n}
                        type="button"
                        className={`rmwb-wa ${next === s.n ? 'is-next' : ''}`}
                        disabled={!can || busy === key}
                        onClick={() => sendWa(f, s.n)}
                        title={s.n === 1 ? 'Open WhatsApp with the offer (the email goes too)' : `${s.label}: ${dayLabel(c.due[s.n])}`}
                      >
                        <FontAwesomeIcon icon={faWhatsapp} /> {s.n}
                        {mailed && <FontAwesomeIcon icon={faEnvelope} className="rmwb-sent-mail" title="Email sent" />}
                      </button>
                    );
                  })}
                  <button type="button" className="rmwb-remove" onClick={() => remove(f)} aria-label={`Remove ${f.child}`}>
                    <FontAwesomeIcon icon={faTrashCan} />
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {toast && (
        <div className={`rmwb-toast ${toast.bad ? 'is-bad' : ''}`} role="status">
          <FontAwesomeIcon icon={toast.bad ? faTriangleExclamation : faCircleCheck} /> {toast.msg}
        </div>
      )}
    </div>
  );
};

/* ---------- /Admin/winback ---------- */
const WinBack = () => {
  const [view, setView] = useState({ kind: 'list' });
  const [list, setList] = useState(null);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setError('');
    try {
      const res = await axios.get(`${API}/campaigns`, auth());
      setList(res.data?.campaigns || []);
    } catch (err) {
      setError(errText(err, 'Couldn’t load the offers.'));
    }
  }, []);

  useEffect(() => {
    if (view.kind === 'list') load();
  }, [view, load]);

  return (
    <div className="rmwb">
      <header className="rmwb-head">
        <h1>
          <FontAwesomeIcon icon={faGift} /> Win-back offers
        </h1>
        <p>Families who did the free class and never enrolled: send them an offer by WhatsApp and email, with 2 reminders before it ends.</p>
      </header>

      {view.kind === 'new' && <NewOffer onCancel={() => setView({ kind: 'list' })} onCreated={(id) => setView({ kind: 'offer', id })} />}
      {view.kind === 'offer' && <OfferView id={view.id} onBack={() => setView({ kind: 'list' })} />}

      {view.kind === 'list' && (
        <>
          <button type="button" className="rmwb-btn rmwb-btn--yellow" onClick={() => setView({ kind: 'new' })}>
            <FontAwesomeIcon icon={faPlus} /> New offer
          </button>
          {error && !list ? (
            <div className="rmwb-empty">
              <FontAwesomeIcon icon={faTriangleExclamation} />
              <p>{error}</p>
            </div>
          ) : !list ? (
            <div className="rmwb-skel" aria-busy="true" />
          ) : list.length === 0 ? (
            <div className="rmwb-empty">
              <FontAwesomeIcon icon={faGift} />
              <p>No offers yet. Create the first one!</p>
            </div>
          ) : (
            <ul className="rmwb-list">
              {list.map((c) => (
                <li key={c.id}>
                  <button type="button" className="rmwb-offer" onClick={() => setView({ kind: 'offer', id: c.id })}>
                    <span className={`rmwb-status is-${c.status}`}>{c.status === 'active' ? 'Running' : c.status === 'ended' ? 'Ended' : 'Stopped'}</span>
                    <b>{c.name}</b>
                    <small>
                      {c.offerPercent ? `${c.offerPercent}% off` : 'No discount'} · until {dayLabel(c.endsOn)}
                    </small>
                    <span className="rmwb-offer-nums">
                      {c.counts.contacted}/{c.counts.total} contacted · <b>{c.counts.enrolled} enrolled</b>
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </div>
  );
};

export default WinBack;
